import { RouterModule, Routes } from '@angular/router';

import { AuthGuard } from './auth.guard';
import { AuthResolveGuard } from './auth-resolve.guard';
import { CompleteProfileContainerComponent } from './containers/complete-profile-container/complete-profile-container.component';
import { LoginGuard } from '@myaccount/app/myaccount/login.guard';
import { MYACCOUNT_HTTP_INTERCEPTOR_PROVIDERS } from './myaccount.interceptors';
import { MyAccountContainerComponent } from './containers/myaccount-container/myaccount-container.component';
import { NgModule } from '@angular/core';
import { ReservationsGuard } from './reservations.guard';

const ROUTES: Routes = [
	{
		path: 'LogonForm',
		loadChildren: () => import('@my-account/templates').then((m) => m.LoginModule),
		data: { title: 'Sign In', meta: 'Sign in to {0} to access your account, check out faster, track and view orders, manage and redeem rewards, and more.' },
		canActivate: [
			LoginGuard
		]
	},
	{
		path: 'EZApplyCreditCard',
		loadChildren: () => import('@my-account/templates').then((m) => m.EzApplyCreditCardModule),
		canActivate: [ AuthGuard ],
	},
	{
		path: 'EZPrequalifyCreditCard',
		loadChildren: () => import('@my-account/templates').then((m) => m.EzApplyCreditCardModule),
		canActivate: [ AuthGuard ],
	},
	{
		path: 'CompleteProfile',
		component: CompleteProfileContainerComponent,
		data: { title: 'Complete Profile', meta: 'Complete your profile' }
	},
	{
		/*
		 * TODO(dpromanko): this component is being nested to temporarily work around a race condition between the AuthGuard and secured-athlete.js from ceops
		 * if this AuthGuard runs before secured-athlete.js loads, the page breaks and renders nothing
		 */
		path: 'ChangeEmail',
		resolve: {authenticated: AuthResolveGuard},
		data: { breadcrumb: 'Change Email' },
		children: [
			{
				path: '',
				loadChildren: () => import('./containers/change-email-container/change-email-container.module').then((m) => m.ChangeEmailContainerModule),
				data: { title: 'Change Email', meta: 'Change your email' },
				canActivate: [
					AuthGuard
				]
			}
		]
	},
	{
		path: 'LogonRedirect',
		loadChildren: () => import('@my-account/templates').then((m) => m.LoginRedirectModule)
	},
	{
		path: 'my-account',
		redirectTo: 'MyAccount/AccountSummary',
		pathMatch: 'full'
	},
	{
		path: 'MyAccount',
		redirectTo: 'MyAccount/AccountSummary',
		pathMatch: 'full'
	},
	{
		path: 'accountsummary',
		redirectTo: 'MyAccount/AccountSummary',
		pathMatch: 'full'
	},
	{
		path: 'MyAccount/Orders/Purchases',
		redirectTo: 'MyAccount/Orders',
		pathMatch: 'full'
	},
	{
		path: 'PersonalInformation',
		redirectTo: 'MyAccount/PersonalInformation',
		pathMatch: 'full'
	},
	{
		path: 'MyAccount/Profile/PersonalInformation',
		redirectTo: 'MyAccount/PersonalInformation',
		pathMatch: 'full'
	},
	{
		path: 'MyAccount/Profile',
		redirectTo: 'MyAccount/PersonalInformation',
		pathMatch: 'full'
	},
	{
		path: 'MyAccount/Profile/Preferences',
		redirectTo: 'MyAccount/Preferences',
		pathMatch: 'full'
	},
	{
		path: 'Preferences',
		redirectTo: 'MyAccount/Preferences',
		pathMatch: 'full'
	},
	{
		path: 'MyAccount/Profile/Addresses',
		redirectTo: 'MyAccount/Addresses',
		pathMatch: 'full'
	},
	{
		path: 'Addresses',
		redirectTo: 'MyAccount/Addresses',
		pathMatch: 'full'
	},
	{
		path: 'AthleteSettings',
		redirectTo: 'MyAccount/Settings',
		pathMatch: 'full'
	},
	{
		path: 'Settings',
		redirectTo: 'MyAccount/Settings',
		pathMatch: 'full'
	},
	{
		path: 'MyAccount/AthleteSettings',
		redirectTo: 'MyAccount/Settings',
		pathMatch: 'full'
	},
	{
		path: 'MyAccount/Profile/Settings',
		redirectTo: 'MyAccount/Settings',
		pathMatch: 'full'
	},
	{
		path: 'MyAccount',
		component: MyAccountContainerComponent,
		resolve: {authenticated: AuthResolveGuard},
		data: { breadcrumb: 'My Account' },
		children: [
			{
				path: 'AccountSummary',
				loadChildren: () => import('@my-account/account-summary/templates').then((m) => m.AccountSummaryModule),
				data: { title: 'Account Summary', breadcrumb: 'Summary' },
				canActivate: [
					AuthGuard
				]
			},
			{
				path: 'ScoreCard',
				loadChildren: () => import('@my-account/rewards/templates').then((m) => m.RewardsModule),
				data: { title: 'ScoreCard', breadcrumb: 'ScoreCard' },
				canActivate: [
					AuthGuard
				]
			},
			{
				path: 'OrderDetail/:orderNumber',
				loadChildren: () => import('@my-account/templates').then((m) => m.OrderDetailModule),
				data: { title: 'Order Details', breadcrumb: 'Orders|Order Details' },
				canActivate: [
					AuthGuard
				]
			},
			{
				path: 'Orders',
				loadChildren: () => import('@my-account/purchases/templates').then((m) => m.PurchasesModule),
				data: { title: 'Orders', breadcrumb: 'Orders' },
				canActivate: [
					AuthGuard
				]
			},
			{
				path: 'Lists',
				data: { breadcrumb: 'Lists' },
				children: [{
					path: '',
					loadChildren: () => import('./containers/list-container/list-container.module').then((m) => m.ListContainerModule),
					data: { title: 'Lists', breadcrumb: '', isListRoute: true},
				},
				{
					path: ':listName',
					loadChildren: () => import('@shared/favorites/templates').then((m) => m.FavoritesModule),
					data: { title: 'Lists', breadcrumb: 'List', isListRoute: true},
				}]
			},
			{
				path: 'Favorites',
				loadChildren: () => import('@shared/favorites/templates').then((m) => m.FavoritesModule),
				data: { breadcrumb: 'Favorites' }
			},
			{
				path: 'Appointments',
				loadChildren: () => import('@my-acount/my-appointments/templates').then((m) => m.MyAppointmentsModule),
				data: { title: 'Appointments', breadcrumb: 'Appointments' },
				canActivate: [
					AuthGuard, ReservationsGuard
				]
			},
			{
				path: 'PersonalInformation',
				loadChildren: () => import ('@my-account/profile/templates').then((m) => m.PersonalInformationModule),
				data: { title: 'Personal Information', breadcrumb: 'Profile|Personal Information'},
				canActivate: [
					AuthGuard
				]
			},
			{
				path: 'Preferences',
				loadChildren: () => import ('@my-account/profile/templates').then((m) => m.PreferencesModule),
				data: { title: 'Preferences', breadcrumb: 'Profile|Preferences'},
				canActivate: [
					AuthGuard
				]
			},
			{
				path: 'Addresses',
				loadChildren: () =>import ('@my-account/profile/templates').then((m) => m.AthleteAddressModule),
				data: { title: 'Addresses', breadcrumb: 'Profile|Addresses'},
				canActivate: [
					AuthGuard
				]
			},
			{
				path: 'Settings',
				loadChildren: () => import('@my-account/profile/components').then((m) => m.AthleteSettingsModule),
				data: { title: 'Settings', breadcrumb: 'Profile|Settings' },
				canActivate: [
					AuthGuard
				]
			},
			{
				path: '',
				loadChildren: () => import('@my-account/account-summary/templates').then((m) => m.AccountSummaryModule),
				data: { title: 'Account Summary', breadcrumb: 'Summary' },
				canActivate: [
					AuthGuard
				]
			},
			{
				path: '**',
				loadChildren: () => import('@my-account/account-summary/templates').then((m) => m.AccountSummaryModule),
				data: { title: 'Account Summary', breadcrumb: 'Summary' },
				canActivate: [
					AuthGuard
				]
			}
		]
	},
	{
		path: 'trackOrder',
		redirectTo: 'TrackOrder',
		pathMatch: 'full'
	},
	{
		path: 'webapp/wcs/stores/servlet/TrackOrder',
		redirectTo: 'TrackOrder',
		pathMatch: 'full'
	},
	{
		path: 'TrackOrder',
		loadChildren: () => import('@my-account/templates').then((m) => m.OrderTrackModule),
		data: {
			title: 'Track Your Order'
		}
	},
	{
		path: 'Lists',
		data: { breadcrumb: 'Lists' },
		children: [
			{
				path: 'shared/:id',
				loadChildren: () => import('@shared/favorites/templates').then((m) => m.FavoritesModule),
				data: { isSharedRoute: true }
			}
		]
	},
	{
		path: 'Favorites',
		data: { breadcrumb: 'Favorites' },
		children: [
			{
				path: '',
				redirectTo: '/MyAccount/Favorites',
				pathMatch: 'full'
			},
			{
				path: 'shared',
				redirectTo: '/MyAccount/Favorites',
				pathMatch: 'full'
			},
			{
				path: 'shared/:id',
				loadChildren: () => import('@shared/favorites/templates').then((m) => m.FavoritesModule),
				data: { isSharedRoute: true }
			}
		]
	},
];

@NgModule({
	imports: [
		RouterModule.forChild(ROUTES)
	],
	exports: [
		RouterModule
	],
	providers: [
		...MYACCOUNT_HTTP_INTERCEPTOR_PROVIDERS,
		AuthResolveGuard
	]
})
export class MyAccountRoutingModule {}
